import type { BacktestPosition, DailyBar, EarningsReportView } from '@fleece/models';
import { X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { MarkerShape, OutcomeMarker, Skeleton } from '@/components/ui';
import { useElementWidth } from '@/hooks/use-element-width';
import { usePreferences } from '@/hooks/use-preferences';
import { fullWindow, isFullWindow, panWindow, visibleRange, zoomWindow, type ChartWindow } from '@/lib/chart-window';
import * as fmt from '@/lib/format';
import { OUTCOMES, outcomeStyle } from '@/lib/outcomes';
import type { ChartStyle } from '@/lib/preferences';
import { barAt, buildPriceChartModel, MARGINS, type PlacedDot } from '@/lib/price-chart-model';

export interface PriceChartProps {
  readonly symbol: string;
  readonly bars: ReadonlyArray<DailyBar>;
  readonly positions: ReadonlyArray<BacktestPosition>;
  /** Reports to mark on their release dates. Empty for a fund, and for a name the calendar lacks. */
  readonly earnings: ReadonlyArray<EarningsReportView>;
  readonly height: number;
  readonly chartStyle: ChartStyle;
}

/** Below this a hollow body is a smudge of outline: thinner candles are drawn solid. */
const HOLLOW_MIN_WIDTH = 3;

/** How far a press must travel before it is a drag rather than a click. */
const DRAG_THRESHOLD_PX = 3;

/** Wheel pixels per e-fold of zoom: a notch of a mouse wheel (~100px) zooms by about a fifth. */
const WHEEL_ZOOM_RATE = 0.002;

interface DragState {
  readonly x: number;
  readonly window: ChartWindow;
  moved: boolean;
}

/**
 * Daily candles with the backtest's positions stacked above their entry days, and the
 * underlying's earnings reports marked where they fell.
 *
 * d3 computes — scales, ticks, where each marker goes — and React owns every element.
 * Candles are neutral ink: the outcome markers are the point of the chart once a
 * backtest has run, and red and green candles would compete with red and green dots.
 *
 * The wheel zooms the time axis around the pointer and a drag pans it; a double-click
 * shows everything again. The price axis fits the days in view. On a touchscreen a
 * sideways drag pans and an upward one still scrolls the page.
 */
export function PriceChart({ symbol, bars, positions, earnings, height, chartStyle }: PriceChartProps) {
  const { date } = usePreferences();
  const [widthRef, width] = useElementWidth<HTMLDivElement>();

  // The window is held with the bars it indexes into, so loading another range or ticker
  // shows everything again by itself rather than through an effect.
  const [view, setView] = useState<{ readonly bars: ReadonlyArray<DailyBar>; readonly window: ChartWindow } | undefined>(undefined);
  const chartWindow = view?.bars === bars ? view.window : fullWindow(bars.length);
  const setWindow = (next: ChartWindow): void => setView({ bars, window: next });
  const range = visibleRange(chartWindow, bars.length);
  const visible = useMemo(() => bars.slice(range.start, range.end), [bars, range.start, range.end]);
  const latestClose = bars.at(-1)?.close;
  const model = useMemo(() => buildPriceChartModel(visible, positions, width, height, latestClose), [visible, positions, width, height, latestClose]);
  const zoomed = !isFullWindow(chartWindow, bars.length);

  const [hover, setHover] = useState<DailyBar | undefined>(undefined);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<DragState | null>(null);

  // Held with the positions it belongs to, so a new backtest drops the selection by
  // itself rather than through an effect.
  const [selection, setSelection] = useState<{ readonly positions: ReadonlyArray<BacktestPosition>; readonly index: number } | undefined>(undefined);
  const selected = selection?.positions === positions ? model.dots.find((dot) => dot.index === selection.index) : undefined;
  const setSelected = (dot: PlacedDot | undefined): void => setSelection(dot === undefined ? undefined : { positions, index: dot.index });

  useEffect(() => {
    if (selected === undefined) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelection(undefined);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  // What the wheel handler needs. It is attached once, natively, so it reads these through a
  // ref rather than closing over one render's values. Each render writes what it drew; the
  // handler writes the window it asks for, so events arriving faster than renders build on
  // each other instead of each starting from the same stale window.
  const latest = useRef({ bars, innerWidth: model.innerWidth, window: chartWindow });
  useEffect(() => {
    latest.current = { bars, innerWidth: model.innerWidth, window: chartWindow };
  });

  // Attached natively rather than through React's `onWheel`, which is passive and so
  // cannot stop the page scrolling while the chart zooms.
  const plotRef = useCallback(
    (element: HTMLDivElement | null) => {
      const stopWidth = widthRef(element);
      if (element === null) {
        return undefined;
      }
      const onWheel = (event: WheelEvent) => {
        const { bars: all, innerWidth, window: now } = latest.current;
        if (all.length === 0 || innerWidth <= 0) {
          return;
        }
        // Firefox reports some wheels in lines rather than pixels.
        const scale = event.deltaMode === 1 ? 16 : 1;
        const next =
          Math.abs(event.deltaX) > Math.abs(event.deltaY)
            ? // A sideways swipe on a trackpad pans, as it would scroll a page.
              panWindow(now, all.length, (event.deltaX * scale) / (innerWidth / (now.end - now.start)))
            : zoomWindow(now, all.length, (event.clientX - element.getBoundingClientRect().left - MARGINS.left) / innerWidth, Math.exp(event.deltaY * scale * WHEEL_ZOOM_RATE));
        // Nothing to zoom or pan — fully out, fully in, or at an end: the scroll is the page's.
        // Taking it anyway would leave a reader unable to scroll past the chart.
        if (next === now) {
          return;
        }
        event.preventDefault();
        latest.current = { ...latest.current, window: next };
        setView({ bars: all, window: next });
      };
      element.addEventListener('wheel', onWheel, { passive: false });
      return () => {
        element.removeEventListener('wheel', onWheel);
        stopWidth?.();
      };
    },
    [widthRef],
  );

  const onPointerDown = (event: PointerEvent<SVGRectElement>) => {
    if (event.button !== 0) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { x: event.clientX, window: chartWindow, moved: false };
  };

  const onPointerMove = (event: PointerEvent<SVGRectElement>) => {
    const pressed = drag.current;
    if (pressed !== null) {
      const dx = event.clientX - pressed.x;
      if (!pressed.moved && Math.abs(dx) > DRAG_THRESHOLD_PX) {
        pressed.moved = true;
        setDragging(true);
        setHover(undefined);
      }
      if (pressed.moved) {
        // Dragging right brings earlier days in, as grabbing the paper would.
        setWindow(panWindow(pressed.window, bars.length, -dx / model.x.step()));
        return;
      }
    }
    const box = event.currentTarget.getBoundingClientRect();
    setHover(barAt(model, visible, event.clientX - box.left));
  };

  const onPointerUp = () => {
    // A press that never moved was a click on empty plot, which dismisses a selected position.
    if (drag.current !== null && !drag.current.moved) {
      setSelected(undefined);
    }
    drag.current = null;
    setDragging(false);
  };

  /**
   * The browser took the gesture — on a phone, a vertical swipe that became a page scroll. It
   * ends the drag, but it was not a click, so a selected position stays selected.
   */
  const onPointerCancel = () => {
    drag.current = null;
    setDragging(false);
  };

  const bandwidth = model.x.bandwidth();
  const center = (day: string): number => (model.x(day) ?? 0) + bandwidth / 2;
  const closeLine = chartStyle === 'candle' ? '' : visible.map((bar, i) => `${i === 0 ? 'M' : 'L'}${center(bar.date)},${model.y(bar.close)}`).join('');
  const reasonsPresent = OUTCOMES.filter((outcome) => positions.some((position) => position.exitReason === outcome.reason));
  const hoverX = hover === undefined ? undefined : center(hover.date);
  const earningsInView = earnings.filter((report) => model.x(report.date) !== undefined);
  const hoverReport = hover === undefined ? undefined : earnings.find((report) => report.date === hover.date);

  return (
    <div className="relative">
      <div className="flex min-h-5 flex-wrap items-center gap-x-3 gap-y-0.5 px-2.5 pt-1.5 text-[11px] text-ink-2 tabular">
        {hover !== undefined ? (
          <>
            <span className="font-semibold text-ink">{date(hover.date)}</span>
            <span>O {fmt.price(hover.open)}</span>
            <span>H {fmt.price(hover.high)}</span>
            <span>L {fmt.price(hover.low)}</span>
            <span>C {fmt.price(hover.close)}</span>
            {hoverReport !== undefined && (
              <span className="font-medium text-ink">
                Earnings {hoverReport.fiscalPeriod}, {hoverReport.timing === 'after-close' ? 'after the close' : 'before the open'}
              </span>
            )}
          </>
        ) : (
          <span>
            <span className="font-semibold text-ink">{symbol}</span> daily, split-adjusted
            {latestClose !== undefined && <span className="ml-2">last close {fmt.price(latestClose)}</span>}
            <span className="ml-2 hidden text-muted sm:inline">scroll to zoom, drag to pan</span>
          </span>
        )}
        <span className="ml-auto flex flex-wrap items-center gap-x-2.5">
          {earningsInView.length > 0 && (
            <span className="inline-flex items-center gap-1">
              <EarningsBadge />
              Earnings
            </span>
          )}
          {reasonsPresent.map((outcome) => (
            <span key={outcome.reason} className="inline-flex items-center gap-1">
              <OutcomeMarker color={outcome.color} shape={outcome.shape} />
              {outcome.label}
            </span>
          ))}
          {zoomed && (
            <button type="button" onClick={() => setWindow(fullWindow(bars.length))} className="rounded px-1 font-medium text-accent hover:underline">
              Reset zoom
            </button>
          )}
        </span>
      </div>
      <div ref={plotRef} className="relative w-full" style={{ height }}>
        {width > 0 && (
          <svg
            width={width}
            height={height}
            role="img"
            aria-label={`${symbol} daily price${positions.length > 0 ? ` with ${positions.length} backtest positions` : ''}`}
            // Vertical swipes still scroll the page; sideways ones reach the chart as a pan.
            style={{ touchAction: 'pan-y' }}
          >
            <g transform={`translate(${MARGINS.left},${MARGINS.top})`}>
              {model.yTicks.map((tick) => (
                <g key={tick} transform={`translate(0,${model.y(tick)})`}>
                  <line x2={model.innerWidth} stroke="var(--grid)" />
                  <text x={model.innerWidth + 4} dy="0.32em" fontSize={10} fill="var(--muted)" className="tabular">
                    {tick}
                  </text>
                </g>
              ))}
              {model.xTicks.map((tick) => (
                <text key={tick} x={center(tick)} y={model.innerHeight + 14} fontSize={10} fill="var(--muted)" textAnchor="middle">
                  {date(tick)}
                </text>
              ))}
              <line y1={model.innerHeight} y2={model.innerHeight} x2={model.innerWidth} stroke="var(--axis)" />

              {earningsInView.map((report) => (
                <line key={report.date} x1={center(report.date)} x2={center(report.date)} y2={model.innerHeight} stroke="var(--axis)" strokeDasharray="1 3" pointerEvents="none" />
              ))}

              {chartStyle === 'candle' ? (
                visible.map((bar) => {
                  const middle = center(bar.date);
                  const up = bar.close >= bar.open;
                  const color = up ? 'var(--candle-up)' : 'var(--candle-down)';
                  const top = model.y(Math.max(bar.open, bar.close));
                  const bodyHeight = Math.max(1, Math.abs(model.y(bar.open) - model.y(bar.close)));
                  const hollow = up && model.candleWidth >= HOLLOW_MIN_WIDTH;
                  return (
                    <g key={bar.date}>
                      <line x1={middle} x2={middle} y1={model.y(bar.high)} y2={model.y(bar.low)} stroke={color} />
                      <rect
                        x={middle - model.candleWidth / 2}
                        y={top}
                        width={model.candleWidth}
                        height={bodyHeight}
                        fill={hollow ? 'var(--surface)' : color}
                        stroke={model.candleWidth >= HOLLOW_MIN_WIDTH ? color : 'none'}
                        strokeWidth={1}
                      />
                    </g>
                  );
                })
              ) : (
                <path d={closeLine} fill="none" stroke="var(--candle-up)" strokeWidth={1.5} strokeLinejoin="round" />
              )}

              {model.lastClose !== undefined && (
                <g pointerEvents="none">
                  <line x2={model.innerWidth} y1={model.lastClose.y} y2={model.lastClose.y} stroke="var(--accent)" strokeWidth={1} strokeDasharray="4 3" />
                  {/* The tag sits over the axis labels, where a price is read. */}
                  <rect x={model.innerWidth + 1} y={model.lastClose.y - 7} width={MARGINS.right - 2} height={14} rx={2} fill="var(--accent)" />
                  <text x={model.innerWidth + 4} y={model.lastClose.y} dy="0.32em" fontSize={10} fontWeight={600} fill="var(--accent-ink)" className="tabular">
                    {fmt.price(model.lastClose.price)}
                  </text>
                </g>
              )}

              {hoverX !== undefined && <line x1={hoverX} x2={hoverX} y2={model.innerHeight} stroke="var(--axis)" strokeDasharray="2 2" pointerEvents="none" />}

              <rect
                width={model.innerWidth}
                height={model.innerHeight}
                fill="transparent"
                className={dragging ? 'cursor-grabbing' : zoomed ? 'cursor-grab' : undefined}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerCancel}
                onPointerLeave={() => setHover(undefined)}
                onDoubleClick={() => setWindow(fullWindow(bars.length))}
              />

              {/* Over the candles, at the foot of the plot, where no position marker sits. */}
              {earningsInView.map((report) => (
                <g key={report.date} transform={`translate(${center(report.date)},${model.innerHeight - 8})`} pointerEvents="none">
                  <EarningsBadgeShape />
                </g>
              ))}

              {model.dots.map((dot) => {
                const style = outcomeStyle(dot.position.exitReason);
                const isSelected = selected?.index === dot.index;
                return (
                  <g key={dot.index} transform={`translate(${dot.x},${dot.y})`} className="cursor-pointer" onClick={() => setSelected(isSelected ? undefined : dot)}>
                    {/* A hit target larger than the mark: a 6px dot is not something a thumb lands on. */}
                    <circle r={6} fill="transparent" />
                    {isSelected && <circle r={5} fill="none" stroke="var(--ink)" strokeWidth={1.5} />}
                    <g stroke="var(--surface)" strokeWidth={0.75}>
                      <MarkerShape color={style.color} shape={style.shape} r={3} />
                    </g>
                  </g>
                );
              })}
            </g>
          </svg>
        )}
        {selected !== undefined && <PositionPopover dot={selected} width={width} onClose={() => setSelected(undefined)} />}
      </div>
    </div>
  );
}

/** An earnings marker: a small "E" tile, the same in the plot and in the legend. */
function EarningsBadgeShape() {
  return (
    <>
      <rect x={-6} y={-6} width={12} height={12} rx={2} fill="var(--surface)" stroke="var(--ink-2)" strokeWidth={1} />
      <text textAnchor="middle" dy="0.35em" fontSize={8} fontWeight={700} fill="var(--ink-2)">
        E
      </text>
    </>
  );
}

function EarningsBadge() {
  return (
    <svg width={12} height={12} viewBox="-6 -6 12 12" aria-hidden className="shrink-0 overflow-visible">
      <EarningsBadgeShape />
    </svg>
  );
}

function PositionPopover({ dot, width, onClose }: { dot: PlacedDot; width: number; onClose: () => void }) {
  const { date } = usePreferences();
  const { position } = dot;
  const style = outcomeStyle(position.exitReason);
  const left = Math.min(Math.max(8, dot.x + MARGINS.left - 110), Math.max(8, width - 228));
  const top = dot.y + MARGINS.top + 10;
  return (
    <div
      role="dialog"
      aria-label="Backtest position"
      className="absolute z-10 w-[220px] rounded-md border border-line bg-surface p-2 text-[11px] shadow-lg tabular"
      style={{ left, top }}
    >
      <div className="mb-1 flex items-center gap-1.5">
        <OutcomeMarker color={style.color} shape={style.shape} />
        <span className="font-semibold text-ink">{style.label}</span>
        {position.daysHeld !== undefined && <span className="text-ink-2">after {fmt.days(position.daysHeld)}</span>}
        <button type="button" aria-label="Close" onClick={onClose} className="ml-auto rounded p-0.5 text-muted hover:text-ink">
          <X className="size-3.5" />
        </button>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-ink-2">
        <dt>Spread</dt>
        <dd className="text-ink">
          {fmt.strike(position.shortStrike)}/{fmt.strike(position.longStrike)} · {date(position.expiration)}
        </dd>
        <dt>Entered</dt>
        <dd className="text-ink">
          {date(position.entryDate)} {fmt.easternTime(position.entryAt)} · {position.dte} DTE
        </dd>
        <dt>Underlying</dt>
        <dd className="text-ink">
          {fmt.price(position.underlyingPrice)} · {fmt.pct(position.shortMoneyness)} OTM
        </dd>
        <dt>Credit</dt>
        <dd className="text-ink">{fmt.price(position.credit)}</dd>
        {position.exitAt !== undefined && (
          <>
            <dt>Closed</dt>
            <dd className="text-ink">
              {date(fmt.easternDate(position.exitAt))} {fmt.easternTime(position.exitAt)} at {fmt.price(position.exitDebit)}
            </dd>
          </>
        )}
        {position.profit !== undefined && (
          <>
            <dt>P&amp;L</dt>
            <dd className={position.profit >= 0 ? 'text-positive' : 'text-negative'}>
              {fmt.dollars(position.profit * 100)} / contract ({fmt.pct(position.profit / position.credit, 0)} of credit)
            </dd>
          </>
        )}
        <dt>Capital</dt>
        <dd className="text-ink">{fmt.dollars(position.maxLoss * 100, 0)} max loss / contract</dd>
        {position.returnOnCapital !== undefined && position.annualizedReturn !== undefined && (
          <>
            <dt>Return</dt>
            <dd className={position.returnOnCapital >= 0 ? 'text-positive' : 'text-negative'}>
              {fmt.pct(position.returnOnCapital, 1)} on capital, {fmt.pct(position.annualizedReturn, 0)} annualized
            </dd>
          </>
        )}
      </dl>
    </div>
  );
}

/** The chart's footprint while its bars load: the readout line and the plot, at the chart's height. */
export function PriceChartSkeleton({ height }: { height: number }) {
  return (
    <div role="status" aria-label="Loading prices">
      <div className="flex min-h-5 items-center px-2.5 pt-1.5">
        <Skeleton className="h-3 w-40" />
      </div>
      <div className="relative" style={{ height }}>
        <Skeleton className="absolute rounded-md" style={{ top: MARGINS.top, right: MARGINS.right, bottom: MARGINS.bottom, left: MARGINS.left }} />
      </div>
    </div>
  );
}

/** A message in the chart's place, holding its height so a failed load does not collapse the page. */
export function PriceChartMessage({ height, children }: { height: number; children: ReactNode }) {
  return (
    <div>
      <div className="min-h-5 pt-1.5" />
      <div className="flex items-center justify-center px-2.5 text-center text-xs text-negative" style={{ height }}>
        {children}
      </div>
    </div>
  );
}
